<template>
  <div
    class="base-accordion-item accordion-item"
    :data-name="name"
  >
    <slot
      name="headerButton"
      :is-active="isActive"
      :toggle="toggle"
    >
      <button
        class="accordion-item__toggle"
        :class="{
          'accordion-item__toggle--styled': styledButton
        }"
        type="button"
        @click="toggle"
      >
        <slot
          name="headerLabel"
          :is-active="isActive"
        />

        <span
          v-show="!isActive"
          class="accordion-item__icon accordion-item__icon--open"
        >
          <slot name="openIcon">
            <icon-plus />
          </slot>
        </span>

        <span
          v-show="isActive"
          class="accordion-item__icon accordion-item__icon--close"
        >
          <slot name="closeIcon">
            <icon-minus />
          </slot>
        </span>
      </button>
    </slot>

    <transition-height :enable-animation="transitionHeight">
      <div
        v-show="isActive"
        class="accordion-item__body"
      >
        <slot
          :is-active="isActive"
          :toggle="toggle"
        />
      </div>
    </transition-height>
  </div>
</template>

<script>

/**
 * Vue: Base accordion item (base-accordion-item)
 * -----------------------------------------------------------------------------
 * An accordion item component to be used inside the `base-accordion`
 * - Component settings: snippet / dynamic / async / scroll
 *
 * @param {String} name - Name for the accordion item. Required.
 * @param {Boolean} [styledButton] - Whether to pre-style the accordion button.
 * @param {Boolean} [transitionHeight] - Whether to enable height transition
 * animation.
 */
import IconMinus from '~icons/directional-navigation/minus.svg'
import IconPlus from '~icons/directional-navigation/plus.svg'
import TransitionHeight from '~async/transition-height/transition-height'

export default {
  name: 'BaseAccordionItem',

  components: {
    IconMinus,
    IconPlus,
    TransitionHeight,
  },

  /**
   * Inject functions that are provided by the `base-accordion` parent
   * component.
   */
  inject: ['getActive', 'register', 'unregister', 'setActive'],

  props: {
    name: {
      type: String,
      default: '',
      required: true,
    },
    styledButton: {
      type: Boolean,
      default: true,
    },
    transitionHeight: {
      type: Boolean,
      default: false,
    },
  },

  computed: {

    /**
     * Compute if the current accordion item is active.
     * @returns {Boolean}
     */
    isActive() {
      return this.getActive(this)
    },
  },

  /**
   * Before mount, register the component into the `base-accordion` component
   * state.
   */
  beforeMount() {
    this.register(this)
  },

  /**
   * Before unmount, remove the component from the `base-accordion` component
   * state.
   */
  beforeUnmount() {
    this.unregister(this)
  },

  methods: {

    /**
     * Toggle the accordion item.
     * - Triggers the `setActive` function from the `base-component`.
     */
    toggle() {
      this.setActive(this, !this.isActive)
    },

    /**
     * Manually set the active state for the accordion item.
     * - Used to force a component open or closed.
     * - Early return used if the requested state matches the current state to
     *   avoid unnecessary calls to `setActive`
     */
    setToggle(isActive) {
      if (isActive === this.isActive) {
        return
      }

      this.setActive(this, isActive)
    },
  },
}
</script>

<style lang="scss">
@import './base-accordion-item';
</style>
